import { Alert, Card, DatePicker, Select, Space } from 'antd'
import { CategoryScale, Chart as ChartJS, Legend, LineElement, LinearScale, PointElement, Tooltip } from 'chart.js'
import dayjs, { type Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import { useEffect, useMemo, useState } from 'react'
import { Line } from 'react-chartjs-2'

import { ReportsService, type ActiveUsersGranularity, type ActiveUsersResponse } from '@services/ReportsService'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend)
dayjs.extend(isoWeek)
dayjs.extend(weekOfYear)

type ActiveUserPoint = {
    period: string
    value: number
}

const DEFAULT_SELECTED_RANGE: [Dayjs, Dayjs] = [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')]

const GRANULARITY_OPTIONS: { label: string; value: ActiveUsersGranularity }[] = [
    { label: 'Daily', value: 'day' },
    { label: 'Weekly', value: 'week' },
    { label: 'Monthly', value: 'month' }
]

const getPickerType = (granularity: ActiveUsersGranularity): 'date' | 'week' | 'month' => {
    if (granularity === 'week') return 'week'
    if (granularity === 'month') return 'month'
    return 'date'
}

const getPickerFormat = (granularity: ActiveUsersGranularity): string => {
    if (granularity === 'week') return 'YYYY-[W]WW'
    if (granularity === 'month') return 'YYYY-MM'
    return 'YYYY-MM-DD'
}

const mapActiveUsersData = (payload: ActiveUsersResponse): ActiveUserPoint[] => {
    return payload.rows
        .map(row => ({
            period: row.periodStart,
            value: row.activeUsers
        }))
        .sort((a, b) => dayjs(a.period).valueOf() - dayjs(b.period).valueOf())
}

export const ActiveUsersTab = () => {
    const [selectedRange, setSelectedRange] = useState<[Dayjs, Dayjs]>(DEFAULT_SELECTED_RANGE)
    const [granularity, setGranularity] = useState<ActiveUsersGranularity>('week')
    const [activeUsersData, setActiveUsersData] = useState<ActiveUserPoint[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetch = async () => {
            try {
                setIsLoading(true)
                setError(null)

                let newStartDate = selectedRange[0]
                let newEndDate = selectedRange[1]

                if (granularity === 'week' || granularity === 'month') {
                    newStartDate = newStartDate.startOf(granularity)
                    newEndDate = newEndDate.endOf(granularity)
                }

                const results = await ReportsService.getActiveUsers({
                    startDate: newStartDate.format('YYYY-MM-DD'),
                    endDate: newEndDate.format('YYYY-MM-DD'),
                    granularity
                })
                setActiveUsersData(mapActiveUsersData(results))
            } catch (err) {
                console.error('Unable to retrieve active users report')
                setError('Unable to retrieve active users report data')
            } finally {
                setIsLoading(false)
            }
        }

        fetch()
    }, [selectedRange, granularity])

    const onDateRangeChange = (range: [Dayjs | null, Dayjs | null] | null) => {
        if (!range || !range[0] || !range[1]) return
        setSelectedRange([range[0], range[1]])
    }

    const lineData = useMemo(
        () => ({
            labels: activeUsersData.map(point => point.period),
            datasets: [
                {
                    label: 'Active users',
                    data: activeUsersData.map(point => point.value),
                    borderColor: '#1677ff',
                    backgroundColor: 'rgba(22, 119, 255, 0.15)',
                    tension: 0.35,
                    pointRadius: 3
                }
            ]
        }),
        [activeUsersData]
    )

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Card>
                <Space wrap size={16}>
                    <DatePicker.RangePicker
                        value={selectedRange}
                        onChange={onDateRangeChange}
                        allowClear={false}
                        picker={getPickerType(granularity)}
                        format={getPickerFormat(granularity)}
                    />
                    <Select
                        style={{ minWidth: 160 }}
                        value={granularity}
                        options={GRANULARITY_OPTIONS}
                        onChange={value => setGranularity(value)}
                    />
                </Space>
            </Card>

            {error ? <Alert type="error" message={error} showIcon /> : null}

            <Card
                loading={isLoading}
                title={`Active Users - ${GRANULARITY_OPTIONS.find(option => option.value === granularity)?.label}`}
            >
                <div style={{ height: 320 }}>
                    <Line
                        data={lineData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: {
                                    display: false
                                }
                            },
                            scales: {
                                y: {
                                    beginAtZero: true,
                                    title: {
                                        display: true,
                                        text: 'Active users'
                                    }
                                },
                                x: {
                                    title: {
                                        display: true,
                                        text: 'Period Start Date'
                                    }
                                }
                            }
                        }}
                    />
                </div>
            </Card>
        </Space>
    )
}
